import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { statsApi, tasksApi, projectsApi } from '../api/client';
import type { Stats, Task, Project } from '../types';
import {
  BarChart3,
  CheckCircle2,
  Folder,
  ListTodo,
  TrendingUp,
  Calendar,
  Target,
} from 'lucide-react';

export function StatsView() {
  // 获取统计数据
  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['stats'],
    queryFn: () => statsApi.getStats(),
  });

  // 获取任务列表
  const { data: tasksData } = useQuery({
    queryKey: ['tasks'],
    queryFn: () => tasksApi.getAll(),
  });

  // 获取项目列表
  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectsApi.getAll(),
  });

  const stats = statsData?.success ? statsData.data : null;
  const tasks = tasksData?.success ? tasksData.data : [];
  const projects = projectsData?.success ? projectsData.data : [];

  // 计算统计数据
  const overviewStats = useMemo(() => {
    const totalTasks = tasks.length;
    const completedTasks = tasks.filter((t) => t.status === 'done').length;
    const inProgressTasks = tasks.filter((t) => t.status === 'in_progress').length;
    const todoTasks = tasks.filter((t) => t.status === 'todo').length;
    const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

    const totalProjects = projects.length;
    const activeProjects = projects.filter((p) => p.status === 'active').length;
    const completedProjects = projects.filter((p) => p.status === 'completed').length;

    return {
      totalTasks,
      completedTasks,
      inProgressTasks,
      todoTasks,
      completionRate,
      totalProjects,
      activeProjects,
      completedProjects,
    };
  }, [tasks, projects]);

  // 优先级分布
  const priorityStats = useMemo(() => {
    const high = tasks.filter((t) => t.priority === 'high').length;
    const medium = tasks.filter((t) => t.priority === 'medium').length;
    const low = tasks.filter((t) => t.priority === 'low').length;
    return { high, medium, low };
  }, [tasks]);

  // 本周数据
  const weeklyData = useMemo(() => {
    const today = new Date();
    const weekData: { date: string; completed: number; created: number }[] = [];

    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];

      const completed = tasks.filter((t) => {
        if (t.status !== 'done') return false;
        const taskDate = t.updatedAt?.split('T')[0];
        return taskDate === dateStr;
      }).length;

      const created = tasks.filter((t) => {
        const taskDate = t.createdAt?.split('T')[0];
        return taskDate === dateStr;
      }).length;

      weekData.push({
        date: date.toLocaleDateString('zh-CN', { weekday: 'short' }),
        completed,
        created,
      });
    }

    return weekData;
  }, [tasks]);

  // 项目统计
  const projectStats = useMemo(() => {
    return projects.map((project) => {
      const projectTasks = tasks.filter((t) => t.projectId === project.id);
      const completed = projectTasks.filter((t) => t.status === 'done').length;
      const total = projectTasks.length;
      const rate = total > 0 ? Math.round((completed / total) * 100) : 0;

      return {
        ...project,
        taskCount: total,
        completedCount: completed,
        completionRate: rate,
      };
    });
  }, [projects, tasks]);

  if (statsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 概览卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">总任务数</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">
                {overviewStats.totalTasks}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <ListTodo className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center gap-4 text-sm">
            <span className="text-green-600 flex items-center gap-1">
              <CheckCircle2 className="w-4 h-4" />
              {overviewStats.completedTasks} 已完成
            </span>
            <span className="text-blue-600 flex items-center gap-1">
              <TrendingUp className="w-4 h-4" />
              {overviewStats.inProgressTasks} 进行中
            </span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">完成率</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">
                {overviewStats.completionRate}%
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Target className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <div className="mt-4">
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-green-500 rounded-full transition-all"
                style={{ width: `${overviewStats.completionRate}%` }}
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">项目数</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">
                {overviewStats.totalProjects}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Folder className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center gap-4 text-sm">
            <span className="text-blue-600">
              {overviewStats.activeProjects} 进行中
            </span>
            <span className="text-green-600">
              {overviewStats.completedProjects} 已完成
            </span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">待办任务</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">
                {overviewStats.todoTasks}
              </p>
            </div>
            <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
              <Calendar className="w-6 h-6 text-amber-600" />
            </div>
          </div>
          <div className="mt-4 text-sm text-gray-500">
            高优先级: {priorityStats.high} 个
          </div>
        </div>
      </div>

      {/* 周统计图表 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-blue-600" />
          本周任务趋势
        </h3>
        <div className="h-64 flex items-end justify-between gap-2">
          {weeklyData.map((day, index) => {
            const maxValue = Math.max(
              ...weeklyData.map((d) => Math.max(d.completed, d.created)),
              1
            );
            const completedHeight = (day.completed / maxValue) * 100;
            const createdHeight = (day.created / maxValue) * 100;

            return (
              <div key={index} className="flex-1 flex flex-col items-center gap-2">
                <div className="w-full flex items-end justify-center gap-1 h-48">
                  <div
                    className="w-4 bg-green-500 rounded-t transition-all"
                    style={{ height: `${completedHeight}%` }}
                    title={`完成: ${day.completed}`}
                  />
                  <div
                    className="w-4 bg-blue-500 rounded-t transition-all"
                    style={{ height: `${createdHeight}%` }}
                    title={`创建: ${day.created}`}
                  />
                </div>
                <span className="text-xs text-gray-500">{day.date}</span>
              </div>
            );
          })}
        </div>
        <div className="flex items-center justify-center gap-6 mt-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 rounded" />
            <span className="text-sm text-gray-600">完成任务</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-blue-500 rounded" />
            <span className="text-sm text-gray-600">新建任务</span>
          </div>
        </div>
      </div>

      {/* 优先级分布和项目进度 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 优先级分布 */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">优先级分布</h3>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-gray-600">高优先级</span>
                <span className="text-sm font-medium text-gray-900">
                  {priorityStats.high}
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-red-500 rounded-full"
                  style={{
                    width: `${
                      overviewStats.totalTasks > 0
                        ? (priorityStats.high / overviewStats.totalTasks) * 100
                        : 0
                    }%`,
                  }}
                />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-gray-600">中优先级</span>
                <span className="text-sm font-medium text-gray-900">
                  {priorityStats.medium}
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-amber-500 rounded-full"
                  style={{
                    width: `${
                      overviewStats.totalTasks > 0
                        ? (priorityStats.medium / overviewStats.totalTasks) * 100
                        : 0
                    }%`,
                  }}
                />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-gray-600">低优先级</span>
                <span className="text-sm font-medium text-gray-900">
                  {priorityStats.low}
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-green-500 rounded-full"
                  style={{
                    width: `${
                      overviewStats.totalTasks > 0
                        ? (priorityStats.low / overviewStats.totalTasks) * 100
                        : 0
                    }%`,
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* 项目进度 */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">项目进度</h3>
          <div className="space-y-4 max-h-64 overflow-y-auto">
            {projectStats.length === 0 ? (
              <p className="text-gray-500 text-center py-4">暂无项目</p>
            ) : (
              projectStats.map((project) => (
                <div key={project.id}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: project.color }}
                      />
                      <span className="text-sm text-gray-600 truncate max-w-[200px]">
                        {project.name}
                      </span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {project.completionRate}%
                    </span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${project.completionRate}%`,
                        backgroundColor: project.color,
                      }}
                    />
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    {project.completedCount}/{project.taskCount} 任务完成
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
